import { useMutation, useQueryClient } from '@tanstack/react-query';

import QueryKeys from 'hooks/queries/keys';
import useGetAxiosInstance from 'hooks/useGetAxiosInstance';
import useMutationWrapper from 'hooks/useMutation';
import { ApiMethods, CuredApiPaths, GuardrailType } from 'models/enums';

import MutationKeys from './keys';

export type GuardrailBody = {
    guardrail_name: string;
    feature: string;
    guardrail_type: GuardrailType;
    system_prompt: string;
    /** Required for input/output types. Not applicable for system-prompt-header/footer types. */
    error_message?: string;
    sort_order?: number | null;
    is_enabled: boolean;
};

export type GuardrailUpdateVariables = { id: number } & GuardrailBody;

export const useCreateGuardrail = () => {
    const queryClient = useQueryClient();
    return useMutationWrapper<GuardrailBody>(MutationKeys.CREATE_GUARDRAIL, CuredApiPaths.AI_GUARDRAILS, {
        onSuccess: () => {
            queryClient.invalidateQueries(QueryKeys.AI_GUARDRAILS);
        },
    });
};

export const useUpdateGuardrail = () => {
    const queryClient = useQueryClient();
    const getApiInstance = useGetAxiosInstance(ApiMethods.PUT);

    return useMutation<unknown, Error, GuardrailUpdateVariables>({
        mutationKey: MutationKeys.UPDATE_GUARDRAIL,
        mutationFn: async ({ id, ...body }) => {
            const apiInstance = await getApiInstance();
            return (await apiInstance.put(`ai-guardrails/${id}`, body)).data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(QueryKeys.AI_GUARDRAILS);
        },
    });
};

export const useDeleteGuardrail = () => {
    const queryClient = useQueryClient();
    const getApiInstance = useGetAxiosInstance(ApiMethods.DELETE);

    return useMutation<unknown, Error, { id: number }>({
        mutationKey: MutationKeys.DELETE_GUARDRAIL,
        mutationFn: async ({ id }) => {
            const apiInstance = await getApiInstance();
            return (await apiInstance.delete(`ai-guardrails/${id}`)).data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(QueryKeys.AI_GUARDRAILS);
        },
    });
};
